import logging from '@sstdev/lib_logging';
import { Card, Icon, View, fromTheme, h1, h2, hooks, styled, webOnlyStyles } from 'lib_ui-primitives';
import { constants, globalConfig } from 'lib_ui-services';
import lodash from 'lodash';
import { createElement as rc, useMemo } from 'react';
import { useTheme } from 'styled-components';
import useDbView from '../../hooks/useDbView';
import useFilterMetadata from '../../hooks/useFilterMetadata';
import useNetwork from '../../hooks/useNetwork';
import DownloadButton from '../actionElement/DownloadButton';
import ChartLimitAllSyncSizes from './Chart.limitAllSyncSizes';
import FilterContainer from './FilterContainer';
const { sortBy } = lodash;
const { useBbState } = hooks;
const { SCOPE, VISIBILITY, DURATION } = constants.retention;

// limitAllSyncSizes is a per-tenant setting persisted into BbState by loadTenantSettings (see
// lib_ui-primitives/src/BbState). Reading it through useBbState (rather than the non-reactive
// globalConfig() snapshot) lets the dispatcher re-render and switch to server aggregation when the
// setting lands post-sync on a cold start, instead of being stuck on the local variant all session.
const TENANT_SETTING_RETENTION = {
    scope: SCOPE.GLOBAL,
    visibility: VISIBILITY.PER_TENANT,
    duration: DURATION.ALWAYS
};

const ChartHeader = styled(Card.Header).attrs({ name: 'chart-header' })`
    padding: ${fromTheme('charts', 'header', 'padding')};
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${fromTheme('charts', 'header', 'gap')};
`;
ChartHeader.displayName = 'ChartHeader';
const HeaderButtons = styled(View).attrs({ name: 'header-buttons' })`
    align-items: flex-end;
    ${({ theme }) => (theme.selectedTheme === 'gl2025' ? '& button { margin: 0 !important; }' : '')};
`;
HeaderButtons.displayName = 'HeaderButtons';

const ChartBody = styled(Card.Body).attrs({ name: 'chart-body' })`
    padding: ${fromTheme('viewPadding')};
`;
ChartBody.displayName = 'ChartBody';

let Widget = styled(Card).attrs({ name: 'chart-widget' })`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    background-color: ${fromTheme('charts', 'backgroundColor')};
`;
Widget = webOnlyStyles(Widget)`
    box-shadow: ${fromTheme('charts', 'boxShadow')};
`;
Widget.displayName = 'ChartWidget';

const Title = styled(h1)`
    color: ${fromTheme('card', 'header', 'textColor')};
    ${fromTheme('charts', 'header', 'font')};
    margin: ${fromTheme('charts', 'header', 'titleMargin')};
    line-height: ${fromTheme('charts', 'header', 'title', 'lineHeight')};
`;

const Centered = styled(View).attrs({ name: 'centered-chart-body' })`
    flex-grow: 1;
    flex-shrink: 1;
    align-items: center;
    align-content: center;
    justify-content: center;
    flex-direction: column;
    padding: ${fromTheme('charts', 'totals', 'padding')};
`;

const NoRecords = styled(h2).attrs({ name: 'no-records' })`
    ${fromTheme('charts', 'header', 'font')};
    color: ${fromTheme('disabledFontColor')};
    text-align: center;
`;

const OfflineIconWrapper = styled(View).attrs({ name: 'offline-chart-icon' })`
    color: ${fromTheme('disabledFontColor')};
    align-items: center;
    justify-content: center;
`;
OfflineIconWrapper.displayName = 'OfflineIconWrapper';

const EMPTY_ARRAY = [];

const _p = { useFilterMetadata, useDbView, useNetwork, globalConfig, useBbState };
export const _private = _p;

function LocalChart({ nestedComponent, ...props }) {
    const {
        children = EMPTY_ARRAY,
        hNode,
        hNode: { id, namespace, relation, limitResults = 21 }
    } = props;

    const hNodeChildren = hNode.children ?? EMPTY_ARRAY;
    const metadataBasedFilters = _p.useFilterMetadata(hNode);
    const { isOnline } = _p.useNetwork();
    const { calculateAggregateResult, viewReady } = _p.useDbView(namespace, relation, `chart-${id}`, hNode);

    const theme = useTheme();

    const aggregateResult = useMemo(() => {
        if (!viewReady) return EMPTY_ARRAY;
        logging.debug(`[CHART] Recalculating chart ${hNode.title} result`);
        const rawData = calculateAggregateResult();
        if (!rawData) return EMPTY_ARRAY;
        const booleanFilter = metadataBasedFilters.groupByBoolean;
        const booleanLabels = booleanFilter
            ? { true: booleanFilter.trueLabel ?? 'Yes', false: booleanFilter.falseLabel ?? 'No' }
            : null;
        const mapValue = v => {
            if (v == null) return '';
            if (booleanLabels && typeof v === 'boolean') return booleanLabels[v] ?? String(v);
            return String(v);
        };
        // Unwind array _id.title values into separate groups, matching server groupBy behavior for array fields.
        // Duplicate titles within a single item are deduplicated; counts from different items with the same title are summed.
        const mergedMap = new Map();
        for (const item of rawData) {
            const raw = item._id?.title;
            const titles = Array.isArray(raw) ? [...new Set(raw.map(mapValue))] : [mapValue(raw)];
            for (const title of titles) {
                const existing = mergedMap.get(title);
                mergedMap.set(title, existing ? { ...existing, count: existing.count + item.count } : { ...item, _id: { title } });
            }
        }
        const normalized = [...mergedMap.values()];
        if (booleanLabels) {
            const presentTitles = new Set(normalized.map(item => item._id.title));
            if (!presentTitles.has(booleanLabels.true)) normalized.push({ _id: { title: booleanLabels.true }, count: 0 });
            if (!presentTitles.has(booleanLabels.false)) normalized.push({ _id: { title: booleanLabels.false }, count: 0 });
        }
        return sortBy(normalized, ['count']).reverse();
    }, [hNode, viewReady, calculateAggregateResult, metadataBasedFilters]);

    const truncatedAggregateResult = useAggregateGroupByResults(aggregateResult || EMPTY_ARRAY, limitResults);

    const filterControls = useMemo(
        () =>
            children.filter(
                child =>
                    (child.props.hNode.hNodeTypeGroup === 'actionElement' &&
                        child.props.hNode.hNodeType !== 'DownloadButton') ||
                    child.props.hNode.hNodeTypeGroup === 'searchPane'
            ),
        [children]
    );

    const downloadButton = hNodeChildren.find(child => child.hNodeType === 'DownloadButton');

    let chartBody;
    if (!isOnline) {
        chartBody = rc(OfflineIconWrapper, null, rc(Icon, { name: 'cloud-off', size: 'lg', color: 'currentColor' }));
    } else if (aggregateResult.length) {
        chartBody = rc(nestedComponent, { ...props, data: truncatedAggregateResult });
    } else if (!viewReady) {
        chartBody = rc(NoRecords, { id }, 'Loading...');
    } else {
        chartBody = rc(NoRecords, { id }, `No ${hNode?.title || 'Records'}`);
    }

    // prettier-ignore
    return rc(Widget, null,
        rc(ChartHeader, null,
            rc(Title, null, hNode?.title),
            rc(HeaderButtons, null,
                rc(FilterContainer, null, filterControls),
                downloadButton && rc(DownloadButton, {
                    id: `download${id}`,
                    hNode: {
                        variant: 'secondary',
                        size:'sm',
                        ...downloadButton,
                        displayTitleOnButton: theme.selectedTheme === 'gl2025',
                        buttonStyle: downloadButton.style,
                        filePrefix: hNode?.title || `chart_${namespace}_${relation}`
                    },
                    data: aggregateResult
                })
            )
        ),
        rc(ChartBody, null,
            rc(Centered, null, chartBody)
        )
    );
}

/**
 * Aggregates an array of groupBy results
 * @param {Object[]} data an array of object to be sorted and trucated
 * @param {number} numberOfResults how many results you want to display before aggregating the rest
 * @param {string} aggregateValueLabel the label to be used for the trucated value
 * @returns {Object[]}
 */
function useAggregateGroupByResults(data, numberOfResults = 0, aggregateValueLabel = 'Other') {
    return useMemo(() => {
        return aggregateGroupByResults(data, numberOfResults, aggregateValueLabel);
    }, [data, numberOfResults, aggregateValueLabel]);
}

export function aggregateGroupByResults(data, numberOfResults, aggregateValueLabel) {
    if (!Array.isArray(data)) {
        throw new Error('The data argument must be an array of Objects');
    }

    if (isNaN(+numberOfResults)) {
        throw new Error('the numberOfResults argument must be a number');
    }
    // if number of results is 0 or the number of results would not end up creating a truncation, we return the input array
    if (+numberOfResults === 0 || data.length <= +numberOfResults) {
        return data;
    }

    numberOfResults--;
    const displayedData = data.slice(0, numberOfResults);
    const aggregatedData = data.slice(numberOfResults);
    const aggregatedValue = aggregatedData.reduce((previousValue, currentValue) => {
        return previousValue + (currentValue.y ?? currentValue.count);
    }, 0);
    return [
        ...displayedData,
        {
            _id: { title: aggregateValueLabel },
            name: aggregateValueLabel,
            y: aggregatedValue
        }
    ];
}

/**
 * Dispatcher: delegates to the server-side Chart when limitAllSyncSizes is enabled,
 * otherwise renders the local LokiJS-backed Chart.
 * Called as Chart(nestedComponent, props) by chart wrapper components (e.g. TotalsElement).
 */
function Chart(nestedComponent, props) {
    // Seed from globalConfig (already correct on warm loads) but read reactively from BbState so a
    // cold sync that delivers limitAllSyncSizes post-mount flips us to the server-aggregation
    // variant instead of leaving the local variant rendered for the whole session.
    const [limitAllSyncSizes] = _p.useBbState(
        _p.globalConfig().tenantSettings?.limitAllSyncSizes ?? false,
        'limitAllSyncSizes',
        TENANT_SETTING_RETENTION
    );
    const ChartImpl = limitAllSyncSizes === true ? ChartLimitAllSyncSizes : LocalChart;
    return rc(ChartImpl, { ...props, nestedComponent });
}

export default Chart;
